import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { IceCreamCone, Star, Heart, Navigation, Sun, X, ZoomIn } from "lucide-react";
import { SITE, IMAGES } from "../data/site";
import { PageHero, Reveal, CallButton, Confetti } from "../components/Shared";

const offerings = [
  { icon: IceCreamCone, title: "Snowballs", text: "Classic shaved ice snowballs made for hot summer days.", bg: "bg-blue-100", color: "text-blue-600" },
  { icon: Star, title: "Stuffed Specialty Snowballs", text: "Special stuffed snowballs for an extra-special treat.", bg: "bg-yellow-100", color: "text-yellow-600" },
  { icon: Heart, title: "Treats & Sweets", text: "A little something sweet to make every visit brighter.", bg: "bg-red-100", color: "text-red-500" },
  { icon: Navigation, title: "Drive-Through Service", text: "In a hurry? Swing through the drive-through and grab your favorite.", bg: "bg-green-100", color: "text-green-600" },
  { icon: Sun, title: "Outdoor Seating", text: "Stay a while and enjoy your snowball in the sunshine.", bg: "bg-blue-100", color: "text-blue-500" },
];

const MenuBoard = () => {
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    if (!zoomed) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setZoomed(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomed]);

  return (
    <section className="relative overflow-hidden px-4 py-16 sm:px-6 sm:py-20" data-testid="menu-board-section">
      <Confetti />
      <div className="relative mx-auto max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 60, rotate: -3, scale: 0.95 }}
          whileInView={{ opacity: 1, y: 0, rotate: 0, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-[2rem] border-4 border-dashed border-yellow-400 bg-white p-3 shadow-2xl shadow-blue-500/20 sm:p-4"
          data-testid="menu-board-frame"
        >
          <button
            type="button"
            onClick={() => setZoomed(true)}
            aria-label="Zoom in on the menu board"
            data-testid="menu-board-zoom-btn"
            className="group relative block w-full cursor-zoom-in rounded-3xl focus:outline-none focus:ring-4 focus:ring-blue-300"
          >
            <motion.img
              src={IMAGES.menu.url}
              alt={IMAGES.menu.alt}
              loading="eager"
              className="w-full rounded-3xl"
              whileHover={{ scale: 1.015 }}
              transition={{ type: "spring", stiffness: 200, damping: 18 }}
              data-testid="menu-board-image"
            />
            <span className="absolute bottom-4 right-4 flex items-center gap-2 rounded-full bg-slate-900/80 px-4 py-2 text-xs font-extrabold text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100">
              <ZoomIn className="h-4 w-4" aria-hidden="true" /> Tap to enlarge
            </span>
          </button>
        </motion.div>
        <p className="mt-6 text-center text-xs font-extrabold uppercase tracking-widest text-slate-500 sm:text-sm">
          The real hand-drawn board from the stand — tap to read every flavor
        </p>
      </div>

      <AnimatePresence>
        {zoomed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] overflow-y-auto overscroll-contain bg-slate-900/90 p-4 sm:p-8"
            onClick={() => setZoomed(false)}
            role="dialog"
            aria-modal="true"
            aria-label="Menu board enlarged"
            data-testid="menu-lightbox"
            data-lenis-prevent
          >
            <button
              type="button"
              onClick={() => setZoomed(false)}
              aria-label="Close enlarged menu"
              data-testid="menu-lightbox-close"
              className="fixed right-4 top-4 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25 focus:outline-none focus:ring-4 focus:ring-yellow-300"
            >
              <X className="h-6 w-6" aria-hidden="true" />
            </button>
            <motion.img
              src={IMAGES.menu.url}
              alt={IMAGES.menu.alt}
              initial={{ opacity: 0, scale: 0.92, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="mx-auto w-full max-w-3xl rounded-2xl shadow-2xl"
              data-testid="menu-lightbox-image"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="mt-4 text-center text-xs font-extrabold uppercase tracking-widest text-white/70">
              Scroll to read the whole board · tap outside to close
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default function Treats() {
  return (
    <>
      <PageHero
        eyebrow="Our menu"
        title="Menu — Snowballs & Treats"
        sub="Pick your flavor & make it sweet! Classic snowballs, stuffed specialty snowballs, and treats — straight from the stand."
        testid="treats-hero"
        bg="bg-yellow-50"
      >
        <CallButton testid="treats-hero-call-btn">Have a Question? Call Ms. Jeanette</CallButton>
      </PageHero>

      <MenuBoard />

      <section className="px-4 py-20 sm:px-6" data-testid="treats-offerings-section">
        <div className="mx-auto grid max-w-7xl gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {offerings.map((item, i) => {
            const Icon = item.icon;
            return (
              <Reveal key={item.title} delay={i * 0.06}>
                <div data-testid={`treats-offering-${i}`} className="h-full rounded-3xl border border-blue-100 bg-white p-7 shadow-lg shadow-blue-500/10 transition-transform duration-200 hover:-translate-y-1.5">
                  <span className={`flex h-14 w-14 items-center justify-center rounded-2xl ${item.bg}`}>
                    <Icon className={`h-7 w-7 ${item.color}`} aria-hidden="true" />
                  </span>
                  <h2 className="mt-5 font-display text-xl font-semibold">{item.title}</h2>
                  <p className="mt-2 text-sm font-semibold text-slate-600">{item.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="bg-slate-900 px-4 py-20 sm:px-6" data-testid="treats-spotlight-section">
        <div className="mx-auto mb-12 max-w-2xl text-center">
          <span className="mb-4 inline-block rounded-full bg-white/10 px-4 py-1.5 text-xs font-extrabold uppercase tracking-widest text-yellow-300">
            Specialty spotlight
          </span>
          <h2 className="text-3xl font-semibold text-white sm:text-4xl lg:text-5xl">Stuffed &amp; Special</h2>
          <p className="mt-4 text-base text-slate-300 md:text-lg">Real specialty snowballs, straight from the stand.</p>
        </div>
        <div className="mx-auto grid max-w-6xl gap-6 sm:grid-cols-3">
          {[
            { image: IMAGES.chantilly, name: "Berry-Topped Specialty", note: "Topped with whipped cream, fresh strawberries, and blueberries" },
            { image: IMAGES.drizzle, name: "Chocolate-Drizzle Stuffed", note: "A stuffed specialty snowball with cookie crumbles" },
            { image: IMAGES.stuffed, name: "Whipped & Stuffed", note: "A stuffed specialty snowball with cookie pieces" },
          ].map((item, i) => (
            <Reveal key={item.name} delay={i * 0.08}>
              <motion.figure
                whileHover={{ y: -6 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className="overflow-hidden rounded-3xl bg-slate-800 shadow-xl shadow-black/30"
                data-testid={`spotlight-card-${i}`}
              >
                <img src={item.image.url} alt={item.image.alt} loading="lazy" className="aspect-[3/4] w-full object-cover" />
                <figcaption className="p-5">
                  <p className="font-display text-lg font-semibold text-white">{item.name}</p>
                  <p className="mt-1 text-sm font-semibold text-slate-400">{item.note}</p>
                </figcaption>
              </motion.figure>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-4 py-20 text-center sm:px-6" data-testid="treats-cta-section">
        <Reveal>
          <h2 className="text-3xl font-semibold sm:text-4xl">Have a Question?</h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-slate-600 md:text-lg">
            Wondering what's on the menu today? Ms. Jeanette is happy to help.
          </p>
          <div className="mt-8">
            <CallButton testid="treats-cta-call-btn">{`Call Ms. Jeanette — ${SITE.phone}`}</CallButton>
          </div>
        </Reveal>
      </section>
    </>
  );
}
